import { Injectable, OnDestroy } from '@angular/core'
import { Observable, of, Subscription } from 'rxjs'
import { map, catchError, switchMap, shareReplay } from 'rxjs/operators'
import { ApiService } from './api.service'
import { AccountService } from '../account/services/account.service'

export enum AccessLevel {
  ACCOUNT_ADMIN = 'account_admin',
  ACCOUNT_READ = 'account_read',
  ACCOUNT_LIMITED = 'account_limited',
  APP_ADMIN = 'app_admin',
  APP_RECOVER = 'app_recover',
  APP_TEST = 'app_test',
  APP_MANAGE = 'app_manage',
  APP_READ = 'app_read',
}

export type AccessLevelValue = `${AccessLevel}`

/**
 * App-level permissions from the API
 * These map to the ArpioPermission enum in the backend (converted to camelCase)
 */
export enum AppPermission {
  READ_APP = 'readApp',
  UPDATE_APP = 'updateApp',
  DELETE_APP = 'deleteApp',
  MANAGE_TEST = 'manageTest',
  MANAGE_RECOVERY = 'manageRecovery',
}

/**
 * Account-level permissions from the API
 * These map to the ArpioPermission enum in the backend (converted to camelCase)
 */
export enum AccountPermission {
  READ_ACCOUNT = 'readAccount',
  CREATE_ACCOUNT = 'createAccount',
  UPDATE_ACCOUNT = 'updateAccount',
  DELETE_ACCOUNT = 'deleteAccount',
  CREATE_APP = 'createApp',
  READ_AUDIT_EVENTS = 'readAuditEvents',
  READ_BILLABLE_RESOURCE_REPORT = 'readBillableResourceReport',
  MANAGE_API_KEYS = 'manageApiKeys',
  MANAGE_INTEGRATIONS = 'manageIntegrations',
  MANAGE_RBAC = 'manageRbac',
  READ_ACCOUNT_SUBJECT = 'readAccountSubject',
  READ_ACCOUNT_SUBJECT_GROUP = 'readAccountSubjectGroup',
  READ_ROLE = 'readRole',
  READ_API_KEY = 'readApiKey',
}

export interface RolePolicy {
  accessLevel: AccessLevelValue
  appIds?: string[]
  appSetIds?: string[]
  allApps?: boolean
}

export interface UserPermissions {
  accountPermissions: Set<string> // Set of account-level permission strings
  appPermissionsMap: Map<string, Set<string>> // Map of appId to set of permission strings
  isLegacyAdmin: boolean // Whether the principal has the legacy admin role (i.e. user:admin_role)
}

@Injectable({
  providedIn: 'root',
})
export class AccessLevelService implements OnDestroy {
  private userPermissionsCache$: Observable<UserPermissions> | null = null
  private accountSubscription: Subscription

  constructor(
    private apiService: ApiService,
    private accountService: AccountService
  ) {
    // Clear cache when account changes
    this.accountSubscription = this.accountService.accountIdStream.subscribe(() => {
      this.userPermissionsCache$ = null
    })
  }

  ngOnDestroy() {
    this.accountSubscription?.unsubscribe()
  }

  /**
   * Get the current user's permissions using the effectivePermissions structure
   * Returns a cached observable so all subscribers share the same API call result
   */
  getUserPermissions(): Observable<UserPermissions> {
    if (!this.userPermissionsCache$) {
      this.userPermissionsCache$ = this.accountService.accountIdStream.pipe(
        switchMap((accountId) => {
          return this.apiService.getAuthenticatedPrincipal().pipe(
            map((principal) => {
              // Principals with the legacy admin role are not authorized by RBAC.
              // We will grant access to everything in the UI but some requests might be blocked by the backend depending
              // on the type of admin role
              const isLegacyAdmin = principal.adminRole != null

              // Extract permission-based data from effectivePermissions
              let accountPermissions = new Set<string>()
              let appPermissionsMap = new Map<string, Set<string>>()

              if (principal.effectivePermissions) {
                // Get account-level permissions
                const accountPerms = principal.effectivePermissions.accounts[accountId]
                if (accountPerms) {
                  accountPermissions = new Set(accountPerms)
                }

                // Get app-level permissions
                for (const [appId, permissions] of Object.entries(principal.effectivePermissions.apps)) {
                  if (permissions && permissions.length > 0) {
                    appPermissionsMap.set(appId, new Set(permissions))
                  }
                }
              }

              // Grant all permissions to legacy admin users
              if (isLegacyAdmin) {
                // Legacy admins get all account permissions
                accountPermissions = new Set(Object.values(AccountPermission))

                // Legacy admins get all app permissions for all apps
                for (const appId of Object.keys(principal.effectivePermissions?.apps || {})) {
                  appPermissionsMap.set(appId, new Set(Object.values(AppPermission)))
                }
              }

              return {
                accountPermissions,
                appPermissionsMap,
                isLegacyAdmin,
              } as UserPermissions
            }),
            catchError((error) => {
              console.error('Error getting user permissions:', error)
              return of({
                accountPermissions: new Set<string>(),
                appPermissionsMap: new Map<string, Set<string>>(),
                isLegacyAdmin: false,
              } as UserPermissions)
            })
          )
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      )
    }
    return this.userPermissionsCache$
  }

  /**
   * Force a refresh of the user permissions cache
   * This should be called when changes occur that affect access levels (e.g., new app created)
   */
  refreshUserPermissions(): void {
    this.userPermissionsCache$ = null
  }

  /**
   * Get unauthorized message for a specific permission
   */
  getUnauthorizedMessage(permission: AccountPermission | AppPermission | string): string {
    const messages: Record<string, string> = {
      [AccountPermission.MANAGE_RBAC]:
        'You need account admin permissions to manage roles, groups, and users. Contact your system administrator to request access.',
      [AccountPermission.READ_ROLE]:
        'You need at least account read permissions to view roles. Contact your system administrator to request access.',
      [AccountPermission.READ_ACCOUNT_SUBJECT]:
        'You need at least account read permissions to view users. Contact your system administrator to request access.',
      [AccountPermission.READ_ACCOUNT_SUBJECT_GROUP]:
        'You need at least account read permissions to view groups. Contact your system administrator to request access.',
      [AccountPermission.MANAGE_API_KEYS]:
        'You need permissions to manage API keys. Contact your system administrator to request access.',
      [AccountPermission.READ_API_KEY]:
        'You need permissions to view API keys. Contact your system administrator to request access.',
      [AccountPermission.UPDATE_ACCOUNT]:
        'You need permissions to update account settings. Contact your system administrator to request access.',
      [AccountPermission.MANAGE_INTEGRATIONS]:
        'You need permissions to manage integrations. Contact your system administrator to request access.',
      [AppPermission.UPDATE_APP]:
        'You need permissions to update application settings. Contact your system administrator to request access.',
      [AppPermission.DELETE_APP]:
        'You need permissions to delete this application. Contact your system administrator to request access.',
      [AppPermission.MANAGE_TEST]:
        'You need permissions to run tests for this application. Contact your system administrator to request access.',
      [AppPermission.MANAGE_RECOVERY]:
        'You need permissions to manage recovery for this application. Contact your system administrator to request access.',
    }

    return (
      messages[permission] ||
      'You do not have permission to perform this action. Contact your system administrator to request access.'
    )
  }

  /**
   * Get access level display name
   */
  getAccessLevelDisplayName(level: AccessLevel | AccessLevelValue): string {
    const displayNames: Record<AccessLevelValue, string> = {
      [AccessLevel.ACCOUNT_ADMIN]: 'Account Administrator',
      [AccessLevel.ACCOUNT_READ]: 'Account Read Only',
      [AccessLevel.ACCOUNT_LIMITED]: 'Account Limited',
      [AccessLevel.APP_ADMIN]: 'Administrator',
      [AccessLevel.APP_RECOVER]: 'Recovery Operator',
      [AccessLevel.APP_TEST]: 'Test Operator',
      [AccessLevel.APP_MANAGE]: 'Manager',
      [AccessLevel.APP_READ]: 'Viewer',
    }

    return displayNames[level as AccessLevelValue] || level
  }

  /**
   * Check if removing a role or user would violate the "at least one account admin" rule
   */
  validateAccountAdminRequirement(
    currentAdminCount: number,
    isRemovingAdmin: boolean
  ): { isValid: boolean; message?: string } {
    if (isRemovingAdmin && currentAdminCount <= 1) {
      return {
        isValid: false,
        message: 'Cannot delete the last Account Admin role. There must be at least one Account Admin at all times.',
      }
    }
    return { isValid: true }
  }

  /**
   * Create a custom sorting function for PrimeNG tables that need to sort by access level hierarchy
   * This method returns a function that can be used as a (sortFunction) handler in PrimeNG tables
   */
  createAccessLevelSortFunction() {
    return (event: any) => {
      const data = event.data
      const field = event.field
      const order = event.order

      if (field === 'accessLevel') {
        // Helper to get sort score for a role or user
        // Account Admin is always highest, otherwise sort by number of access levels (more = higher)
        const getSortScore = (item: any): number => {
          // Handle single accessLevel (old format or legacy data)
          if (item.accessLevel) {
            // Account admin gets highest score
            if (item.accessLevel === AccessLevel.ACCOUNT_ADMIN) {
              return Number.MAX_SAFE_INTEGER
            }
            // Others get score of 1 (single access level)
            return 1
          }

          // Handle accessLevels array (multiple access levels)
          if (item.accessLevels && Array.isArray(item.accessLevels) && item.accessLevels.length > 0) {
            // If user has account admin, they get highest score
            const hasAccountAdmin = item.accessLevels.some(
              (levelInfo: any) => levelInfo.accessLevel === AccessLevel.ACCOUNT_ADMIN
            )
            if (hasAccountAdmin) {
              return Number.MAX_SAFE_INTEGER
            }

            // Otherwise, score is the count of access levels they have
            return item.accessLevels.length
          }

          return -1
        }

        data.sort((a: any, b: any) => {
          const aScore = getSortScore(a)
          const bScore = getSortScore(b)

          if (aScore === -1 && bScore === -1) {
            return 0
          } else if (aScore === -1) {
            return 1
          } else if (bScore === -1) {
            return -1
          }

          return (aScore - bScore) * order
        })
      } else {
        // Use default sorting for other fields
        data.sort((a: any, b: any) => {
          let aVal = a[field]
          let bVal = b[field]

          if (aVal == null && bVal != null) {
            return -1
          } else if (aVal != null && bVal == null) {
            return 1
          } else if (aVal == null && bVal == null) {
            return 0
          }

          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal) * order
          }

          return (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) * order
        })
      }
    }
  }

  /**
   * Get the access level hierarchy used for sorting and comparisons
   */
  getAccessLevelHierarchy(): AccessLevelValue[] {
    return [
      AccessLevel.ACCOUNT_LIMITED,
      AccessLevel.ACCOUNT_READ,
      AccessLevel.APP_READ,
      AccessLevel.APP_MANAGE,
      AccessLevel.APP_TEST,
      AccessLevel.APP_RECOVER,
      AccessLevel.APP_ADMIN,
      AccessLevel.ACCOUNT_ADMIN,
    ]
  }

  /**
   * Check if a role is a default account role (system role)
   * This method handles any role-like object that has an isSystemRole property
   */
  isDefaultAccountRole(role: any): boolean {
    if (!role) {
      return false
    }
    return role.isSystemRole || false
  }

  isAccountLevelAccess(level: AccessLevel | AccessLevelValue): boolean {
    return [AccessLevel.ACCOUNT_ADMIN, AccessLevel.ACCOUNT_READ, AccessLevel.ACCOUNT_LIMITED].includes(
      level as AccessLevel
    )
  }

  isAppLevelAccess(level: AccessLevel | AccessLevelValue): boolean {
    return !this.isAccountLevelAccess(level)
  }

  /**
   * Check if the user has a specific app permission
   */
  hasAppPermission(appId: string, permission: AppPermission | string): Observable<boolean> {
    return this.getUserPermissions().pipe(
      map((permissions) => {
        // If RBAC is explicitly disabled (false), grant all permissions
        // If RBAC is null/undefined, enforce permissions
        if (this.accountService.rbacEnabled === false) {
          return true
        }

        if (permissions.isLegacyAdmin) {
          return true
        }

        // Check app-specific permissions
        const appPerms = permissions.appPermissionsMap?.get(appId)
        return appPerms?.has(permission) ?? false
      })
    )
  }

  /**
   * Check if the user has a specific account permission
   */
  hasAccountPermission(permission: AccountPermission | string): Observable<boolean> {
    return this.getUserPermissions().pipe(
      map((permissions) => {
        // If RBAC is explicitly disabled (false), grant all permissions
        // If RBAC is null/undefined, enforce permissions
        if (this.accountService.rbacEnabled === false) {
          return true
        }

        if (permissions.isLegacyAdmin) {
          return true
        }

        return permissions.accountPermissions?.has(permission) ?? false
      })
    )
  }
}
